export const en = {
  menu: {
    root: 'Production',
    workOrders: 'Work Orders',
    processingTypes: 'Processing Types',
    tollAgreements: 'Outbound Tolling',
    tollProcessingAgreements: 'Inbound Tolling',
  },
  workOrdersView: {
    title: 'Work Orders',
    subtitle: 'Manage processing work orders',
    createWorkOrder: 'Create Work Order',
    filters: {
      search: 'Search work orders...',
      allStatuses: 'All Statuses',
      allProcessingTypes: 'All Processing Types',
      allFacilities: 'All Facilities',
    },
    summary: {
      total: 'Total',
      draft: 'Draft',
      open: 'Open',
      inProgress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    },
    table: {
      aria: 'Work Orders List',
      columns: {
        workOrderNumber: 'Work Order #',
        status: 'Status',
        processingType: 'Processing Type',
        facility: 'Facility',
        scheduledStart: 'Scheduled Start',
        scheduledEnd: 'Scheduled End',
        createdAt: 'Created',
      },
    },
    statusTypes: {
      Draft: 'Draft',
      Open: 'Open',
      InProgress: 'In Progress',
      Completed: 'Completed',
      Cancelled: 'Cancelled',
    },
    tollingTypes: {
      None: 'None',
      Outbound: 'Outbound (External Processing)',
      Inbound: 'Inbound (Customer Tolling)',
    },
    content: {
      error: (message: string) => `Error loading work orders: ${message}`,
    },
    emptyState: {
      title: 'No work orders found',
      subtitle:
        'No work orders match your current filters. Try adjusting your search criteria or create a new work order.',
    },
    dialog: {
      createTitle: 'Create Work Order',
      editTitle: 'Edit Work Order',
      processingTypeLabel: 'Processing Type',
      processingTypePlaceholder: 'Select processing type...',
      facilityLabel: 'Facility',
      facilityPlaceholder: 'Select facility...',
      scheduledStartLabel: 'Start Date',
      scheduledStartTimeLabel: 'Start Time',
      scheduledEndLabel: 'End Date',
      scheduledEndTimeLabel: 'End Time',
      notesLabel: 'Notes',
      notesPlaceholder: 'Enter notes...',
      isTollingLabel: 'Inbound Tolling (Processing Customer Material)',
      tollAgreementLabel: 'Toll Agreement',
      tollAgreementPlaceholder: 'Select toll agreement...',
      cancel: 'Cancel',
      create: 'Create Work Order',
      creating: 'Creating...',
      update: 'Update Work Order',
      updating: 'Updating...',
      createSuccess: 'Work order created successfully',
      updateSuccess: 'Work order updated successfully',
      createError: (message: string) =>
        `Failed to create work order: ${message}`,
      updateError: (message: string) =>
        `Failed to update work order: ${message}`,
      validation: {
        processingTypeRequired: 'Processing type is required',
        facilityRequired: 'Facility is required',
        inputItemRequired: 'Please select an item for all input items',
        outputItemRequired: 'Please select an item for all output items',
        quantityRequired: 'Quantity must be greater than 0',
      },
      sourceLot: {
        title: 'Source Lot (Optional)',
        description:
          'Select a lot to use all its contents as input, or skip to manually add input items.',
        selectLabel: 'Source Lot',
        selectPlaceholder: 'Select lot to consume...',
      },
      inputItems: {
        title: 'Input Items',
        add: 'Add Input',
        empty:
          'No input items added. Click "Add Input" to add materials to consume.',
        emptyLot: 'Selected lot has no items.',
        selectItem: 'Select item...',
        quantity: 'Qty',
        unit: 'Unit',
        notes: 'Notes',
        remove: 'Remove input item',
      },
      outputItems: {
        title: 'Output Items',
        add: 'Add Output',
        empty:
          'No output items added. Click "Add Output" to add expected outputs.',
        selectItem: 'Select item...',
        quantity: 'Qty',
        unit: 'Unit',
        notes: 'Notes',
        remove: 'Remove output item',
      },
      calculation: {
        title: 'Calculate from Recipe',
        noRecipe: 'This processing type has no recipe defined.',
        hasRecipe:
          'This processing type has a recipe. You can auto-calculate quantities.',
        direction: 'Calculate Direction',
        fromInputs: 'From Inputs',
        fromInputsDesc: 'Enter input quantity, calculate expected outputs',
        fromOutputs: 'From Outputs',
        fromOutputsDesc: 'Enter desired output, calculate required inputs',
        referenceItem: 'Reference Item',
        referenceQuantity: 'Quantity',
        referenceUnit: 'Unit',
        apply: 'Apply Recipe',
        applying: 'Applying...',
        scaleFactor: 'Scale Factor',
        clear: 'Clear Items',
        applySuccess: 'Recipe applied successfully',
        selectProcessingTypeFirst:
          'Select a processing type to use recipe calculation',
      },
    },
    actions: {
      open: 'Open',
      start: 'Start Processing',
      complete: 'Complete',
      cancel: 'Cancel',
    },
  },
  workOrderDetailView: {
    title: (workOrderNumber: string) => `Work Order ${workOrderNumber}`,
    backToList: 'Back to Work Orders',
    tabs: {
      overview: 'Overview',
      inputItems: 'Input Items',
      outputItems: 'Output Items',
    },
    overview: {
      processingType: 'Processing Type',
      facility: 'Facility',
      scheduledStart: 'Scheduled Start',
      scheduledEnd: 'Scheduled End',
      actualStart: 'Actual Start',
      actualEnd: 'Actual End',
      notes: 'Notes',
      isTolling: 'External Processing',
      tollAgreement: 'Toll Agreement',
      createdBy: 'Created By',
    },
    inputItems: {
      title: 'Input Items',
      add: 'Add Input Item',
      table: {
        aria: 'Input Items Table',
        columns: {
          item: 'Item',
          lot: 'Lot',
          tag: 'Tag',
          plannedQuantity: 'Planned Qty',
          actualQuantity: 'Actual Qty',
          unitCost: 'Unit Cost',
          notes: 'Notes',
        },
      },
      emptyState: {
        title: 'No input items',
        subtitle: 'Add input items to this work order.',
      },
    },
    outputItems: {
      title: 'Output Items',
      add: 'Add Output Item',
      table: {
        aria: 'Output Items Table',
        columns: {
          item: 'Item',
          targetLot: 'Target Lot',
          targetTag: 'Target Tag',
          expectedQuantity: 'Expected Qty',
          actualQuantity: 'Actual Qty',
          yieldPercentage: 'Yield %',
          notes: 'Notes',
        },
      },
      emptyState: {
        title: 'No output items',
        subtitle: 'Add output items to this work order.',
      },
    },
    itemsEdit: {
      editButton: 'Edit Items',
      save: 'Save',
      cancel: 'Cancel',
      saving: 'Saving...',
      saveSuccess: 'Items updated successfully',
      saveError: (message: string) => `Failed to update items: ${message}`,
      addInput: 'Add Input',
      addOutput: 'Add Output',
      confirmCancel: 'Discard unsaved changes?',
    },
    completionDialog: {
      title: 'Complete Work Order',
      subtitle: 'Enter actual quantities for completion',
      inputItemsTitle: 'Input Items - Actual Quantities',
      outputItemsTitle: 'Output Items - Actual Quantities',
      itemColumn: 'Item',
      plannedColumn: 'Planned',
      expectedColumn: 'Expected',
      actualColumn: 'Actual Qty',
      unitColumn: 'Unit',
      lotColumn: 'Lot',
      targetLotColumn: 'Target Lot',
      createNewLot: 'Create New Lot',
      selectLot: 'Select lot...',
      selectTag: 'Select tag...',
      tagColumn: 'Tag',
      targetTagColumn: 'Target Tag',
      cancel: 'Cancel',
      complete: 'Complete Work Order',
      completing: 'Completing...',
      completeSuccess: 'Work order completed successfully',
      completeError: (message: string) =>
        `Failed to complete work order: ${message}`,
      validation: {
        actualQuantityRequired: 'Actual quantity is required for all items',
      },
    },
  },
  processingTypesView: {
    title: 'Processing Types',
    subtitle: 'Configure available processing operations',
    createProcessingType: 'Create Processing Type',
    filters: {
      search: 'Search processing types...',
      showInactive: 'Show inactive',
    },
    table: {
      aria: 'Processing Types List',
      columns: {
        name: 'Name',
        code: 'Code',
        description: 'Description',
        isActive: 'Active',
        createdAt: 'Created',
      },
    },
    content: {
      error: (message: string) => `Error loading processing types: ${message}`,
    },
    emptyState: {
      title: 'No processing types found',
      subtitle:
        'No processing types match your current filters. Try adjusting your search criteria or create a new processing type.',
    },
    dialog: {
      createTitle: 'Create Processing Type',
      editTitle: 'Edit Processing Type',
      nameLabel: 'Name',
      namePlaceholder: 'Enter name...',
      codeLabel: 'Code',
      codePlaceholder: 'Enter code (e.g., SHRED)',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'Enter description...',
      isActiveLabel: 'Active',
      cancel: 'Cancel',
      create: 'Create',
      creating: 'Creating...',
      update: 'Update',
      updating: 'Updating...',
      createSuccess: 'Processing type created successfully',
      updateSuccess: 'Processing type updated successfully',
      createError: (message: string) =>
        `Failed to create processing type: ${message}`,
      updateError: (message: string) =>
        `Failed to update processing type: ${message}`,
      validation: {
        nameRequired: 'Name is required',
        codeRequired: 'Code is required',
      },
      recipe: {
        inputsTitle: 'Recipe Inputs',
        outputsTitle: 'Recipe Outputs',
        addInput: 'Add Input',
        addOutput: 'Add Output',
        selectItem: 'Select item...',
        yieldPercentage: 'Yield %',
        outputUnit: 'Unit',
        isPrimary: 'Primary',
        isNoCost: 'No Cost',
        costAllocation: 'Cost %',
        notes: 'Notes',
        noRecipe: 'No recipe defined',
        emptyInputs:
          'No inputs defined. Click "Add Input" to define recipe inputs.',
        emptyOutputs:
          'No outputs defined. Click "Add Output" to define recipe outputs.',
        removeInput: 'Remove input',
        removeOutput: 'Remove output',
        inputHeaders: {
          item: 'Item',
          primary: 'Primary',
          notes: 'Notes',
        },
        outputHeaders: {
          item: 'Item',
          yieldPercentage: 'Yield %',
          outputUnit: 'Unit',
          primary: 'Primary',
          noCost: 'No Cost',
          costPercent: 'Cost %',
          notes: 'Notes',
        },
        validation: {
          needsPrimaryInput: 'Recipe must have exactly one primary input',
          noCostBearingOutputs:
            'Recipe must have at least one cost-bearing output',
          allocationMustSum100: 'Cost allocation percentages must sum to 100%',
          inputItemRequired: 'Please select an item for all inputs',
          outputItemRequired: 'Please select an item for all outputs',
          yieldPercentageRequired: 'Yield percentage must be between 0 and 100',
        },
      },
    },
  },
  tollAgreementsView: {
    title: 'Outbound Tolling',
    subtitle: 'Agreements for sending material to external processors',
    createTollAgreement: 'Create Agreement',
    filters: {
      search: 'Search toll agreements...',
      allProcessingTypes: 'All Processing Types',
      showInactive: 'Show inactive',
    },
    table: {
      aria: 'Toll Agreements List',
      columns: {
        agreementNumber: 'Agreement #',
        processor: 'Processor',
        processingType: 'Processing Type',
        effectiveDate: 'Effective Date',
        expirationDate: 'Expiration Date',
        pricePerUnit: 'Price/Unit',
        isActive: 'Active',
      },
    },
    content: {
      error: (message: string) => `Error loading toll agreements: ${message}`,
    },
    emptyState: {
      title: 'No toll agreements found',
      subtitle:
        'No toll agreements match your current filters. Try adjusting your search criteria or create a new toll agreement.',
    },
    dialog: {
      createTitle: 'Create Outbound Tolling Agreement',
      editTitle: 'Edit Outbound Tolling Agreement',
      agreementNumberLabel: 'Agreement Number',
      agreementNumberPlaceholder: 'Enter agreement number...',
      processorLabel: 'Processor',
      processorPlaceholder: 'Select processor...',
      processingTypeLabel: 'Processing Type',
      processingTypePlaceholder: 'Select processing type...',
      effectiveDateLabel: 'Effective Date',
      expirationDateLabel: 'Expiration Date',
      pricePerUnitLabel: 'Price Per Unit',
      priceUnitLabel: 'Price Unit',
      priceUnitPlaceholder: 'e.g., lb, ton',
      expectedYieldLabel: 'Expected Yield %',
      termsLabel: 'Terms',
      termsPlaceholder: 'Enter terms...',
      isActiveLabel: 'Active',
      facilityLabel: 'Facility',
      cancel: 'Cancel',
      create: 'Create',
      creating: 'Creating...',
      update: 'Update',
      updating: 'Updating...',
      createSuccess: 'Outbound tolling agreement created successfully',
      updateSuccess: 'Outbound tolling agreement updated successfully',
      createError: (message: string) =>
        `Failed to create outbound tolling agreement: ${message}`,
      updateError: (message: string) =>
        `Failed to update outbound tolling agreement: ${message}`,
      validation: {
        agreementNumberRequired: 'Agreement number is required',
        processorRequired: 'Processor is required',
        processingTypeRequired: 'Processing type is required',
        effectiveDateRequired: 'Effective date is required',
      },
    },
  },
  tollProcessingAgreementsView: {
    title: 'Inbound Tolling',
    subtitle: 'Agreements for processing material on behalf of customers',
    createTollProcessingAgreement: 'Create Agreement',
    filters: {
      search: 'Search toll processing agreements...',
      allProcessingTypes: 'All Processing Types',
      allCustomers: 'All Customers',
      showInactive: 'Show inactive',
    },
    table: {
      aria: 'Toll Processing Agreements List',
      columns: {
        agreementNumber: 'Agreement #',
        customer: 'Customer',
        processingType: 'Processing Type',
        effectiveDate: 'Effective Date',
        expirationDate: 'Expiration Date',
        pricePerUnit: 'Price/Unit',
        isActive: 'Active',
      },
    },
    content: {
      error: (message: string) =>
        `Error loading toll processing agreements: ${message}`,
    },
    emptyState: {
      title: 'No toll processing agreements found',
      subtitle:
        'No toll processing agreements match your current filters. Try adjusting your search criteria or create a new agreement.',
    },
    dialog: {
      createTitle: 'Create Inbound Tolling Agreement',
      editTitle: 'Edit Inbound Tolling Agreement',
      agreementNumberLabel: 'Agreement Number',
      agreementNumberPlaceholder: 'Enter agreement number...',
      customerLabel: 'Customer',
      customerPlaceholder: 'Select customer...',
      processingTypeLabel: 'Processing Type',
      processingTypePlaceholder: 'Select processing type...',
      effectiveDateLabel: 'Effective Date',
      expirationDateLabel: 'Expiration Date',
      pricePerUnitLabel: 'Processing Fee Per Unit',
      priceUnitLabel: 'Fee Unit',
      priceUnitPlaceholder: 'e.g., lb, ton',
      expectedYieldLabel: 'Expected Yield %',
      invoicePricingBasisLabel: 'Invoice Pricing Basis',
      termsLabel: 'Terms',
      termsPlaceholder: 'Enter terms...',
      isActiveLabel: 'Active',
      facilityLabel: 'Facility',
      cancel: 'Cancel',
      create: 'Create',
      creating: 'Creating...',
      update: 'Update',
      updating: 'Updating...',
      createSuccess: 'Inbound tolling agreement created successfully',
      updateSuccess: 'Inbound tolling agreement updated successfully',
      createError: (message: string) =>
        `Failed to create inbound tolling agreement: ${message}`,
      updateError: (message: string) =>
        `Failed to update inbound tolling agreement: ${message}`,
      validation: {
        agreementNumberRequired: 'Agreement number is required',
        customerRequired: 'Customer is required',
        processingTypeRequired: 'Processing type is required',
        effectiveDateRequired: 'Effective date is required',
      },
    },
  },
  tollReceiptsView: {
    title: 'Toll Receipts',
    subtitle: 'Manage inbound toll receipt records',
    createTollReceipt: 'Create Receipt',
    filters: {
      search: 'Search toll receipts...',
      allCustomers: 'All Customers',
      allAgreements: 'All Agreements',
      allFacilities: 'All Facilities',
      allStatuses: 'All Statuses',
    },
    table: {
      aria: 'Toll Receipts List',
      columns: {
        receiptNumber: 'Receipt #',
        customer: 'Customer',
        facility: 'Facility',
        receivedAt: 'Received At',
        itemCount: 'Items',
        status: 'Status',
      },
    },
    content: {
      error: (message: string) => `Error loading toll receipts: ${message}`,
    },
    emptyState: {
      title: 'No toll receipts found',
      subtitle:
        'No toll receipts match your current filters. Try adjusting your search criteria or create a new toll receipt.',
    },
    dialog: {
      createTitle: 'Create Toll Receipt',
      editTitle: 'Edit Toll Receipt',
      receiptNumberLabel: 'Receipt Number',
      receiptNumberPlaceholder: 'Enter receipt number...',
      customerLabel: 'Customer',
      customerPlaceholder: 'Select customer...',
      facilityLabel: 'Facility',
      facilityPlaceholder: 'Select facility...',
      agreementLabel: 'Inbound Tolling Agreement',
      receivedAtLabel: 'Received At',
      statusLabel: 'Status',
      notesLabel: 'Notes',
      notesPlaceholder: 'Enter notes...',
      itemsLabel: 'Items',
      addItem: 'Add Item',
      noItems: 'No items added. Click "Add Item" to add materials.',
      cancel: 'Cancel',
      create: 'Create',
      creating: 'Creating...',
      update: 'Update',
      updating: 'Updating...',
      createSuccess: 'Toll receipt created successfully',
      updateSuccess: 'Toll receipt updated successfully',
      createError: (message: string) =>
        `Failed to create toll receipt: ${message}`,
      updateError: (message: string) =>
        `Failed to update toll receipt: ${message}`,
      validation: {
        receiptNumberRequired: 'Receipt number is required',
        customerRequired: 'Customer is required',
        facilityRequired: 'Facility is required',
      },
    },
  },
  tollShipmentsView: {
    title: 'Toll Shipments',
    subtitle: 'Manage outbound toll shipment records',
    createTollShipment: 'Create Shipment',
    filters: {
      search: 'Search toll shipments...',
      allCustomers: 'All Customers',
      allAgreements: 'All Agreements',
      allFacilities: 'All Facilities',
      allStatuses: 'All Statuses',
    },
    table: {
      aria: 'Toll Shipments List',
      columns: {
        shipmentNumber: 'Shipment #',
        customer: 'Customer',
        facility: 'Facility',
        shippedAt: 'Shipped At',
        itemCount: 'Items',
        processingFee: 'Processing Fee',
        status: 'Status',
      },
    },
    content: {
      error: (message: string) => `Error loading toll shipments: ${message}`,
    },
    emptyState: {
      title: 'No toll shipments found',
      subtitle:
        'No toll shipments match your current filters. Try adjusting your search criteria or create a new toll shipment.',
    },
    dialog: {
      createTitle: 'Create Toll Shipment',
      editTitle: 'Edit Toll Shipment',
      shipmentNumberLabel: 'Shipment Number',
      shipmentNumberPlaceholder: 'Enter shipment number...',
      customerLabel: 'Customer',
      customerPlaceholder: 'Select customer...',
      facilityLabel: 'Facility',
      facilityPlaceholder: 'Select facility...',
      agreementLabel: 'Inbound Tolling Agreement',
      shippedAtLabel: 'Shipped At',
      statusLabel: 'Status',
      processingFeeLabel: 'Processing Fee',
      currencyLabel: 'Currency',
      notesLabel: 'Notes',
      notesPlaceholder: 'Enter notes...',
      itemsLabel: 'Items',
      addItem: 'Add Item',
      noItems: 'No items added. Click "Add Item" to add materials.',
      cancel: 'Cancel',
      create: 'Create',
      creating: 'Creating...',
      update: 'Update',
      updating: 'Updating...',
      createSuccess: 'Toll shipment created successfully',
      updateSuccess: 'Toll shipment updated successfully',
      createError: (message: string) =>
        `Failed to create toll shipment: ${message}`,
      updateError: (message: string) =>
        `Failed to update toll shipment: ${message}`,
      validation: {
        shipmentNumberRequired: 'Shipment number is required',
        customerRequired: 'Customer is required',
        facilityRequired: 'Facility is required',
      },
    },
  },
  units: {
    pounds: 'Pounds',
    kilograms: 'Kilograms',
    metricTons: 'MT',
    shortTons: 'ST',
    each: 'Each',
    lb: 'LB',
    kg: 'KG',
    mt: 'MT',
    st: 'ST',
    ea: 'EA',
  },
  statuses: {
    draft: 'Draft',
    received: 'Received',
    processing: 'Processing',
    completed: 'Completed',
    cancelled: 'Cancelled',
    shipped: 'Shipped',
    delivered: 'Delivered',
  },
  itemFields: {
    itemId: 'Item ID',
    quantity: 'Quantity',
    unit: 'Unit',
    removeItem: 'Remove item',
    none: 'None',
    selectItem: 'Select item...',
  },
};

export type TranslationsType = typeof en;
